import { useState } from "react";
import { Icons } from "./shared";

const GEN_STEPS = [
  { label: "Saving restaurant data" },
  { label: "Writing employee records" },
  { label: "Loading staffing requirements" },
  { label: "Filling shifts" },
  { label: "Optimizing distribution" },
  { label: "Running conflict checks" },
  { label: "Calculating score" },
];

export default function Step5Generate({ data, isMobile, onGoToStep, onBuildSchedule, onFineTune }) {
  const [phase, setPhase] = useState("review"); // review | generating | success
  const [genStep, setGenStep] = useState(0);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState(null);
  const [scoreCount, setScoreCount] = useState(0);

  const totalShifts = Object.values(data.staffing).reduce((a, b) => a + b, 0);
  const empCount = data.employees.filter(e => e.name).length;
  const activeRoles = data.roles.filter(r => r.active);
  const openDays = data.dayData.filter(d => d.isOpen).length;

  const handleBuildNow = async () => {
    setPhase("generating");

    // Animate progress while real work happens
    let stepIdx = 0;
    const stepInterval = setInterval(() => {
      stepIdx = Math.min(stepIdx + 1, GEN_STEPS.length - 1);
      setGenStep(stepIdx);
      setProgress(Math.min(((stepIdx + 1) / GEN_STEPS.length) * 90, 90));
    }, 600);

    try {
      const res = await onBuildSchedule();
      clearInterval(stepInterval);
      setGenStep(GEN_STEPS.length - 1);
      setProgress(100);

      setTimeout(() => {
        setResult(res);
        setPhase("success");
        // Animate score count up
        const target = res.score;
        const start = Date.now(), dur = 1200;
        const siv = setInterval(() => {
          const e = Date.now() - start, p = Math.min(e / dur, 1);
          setScoreCount(Math.round((1 - Math.pow(1 - p, 3)) * target));
          if (p >= 1) clearInterval(siv);
        }, 30);
      }, 400);
    } catch (err) {
      clearInterval(stepInterval);
      console.error("Generation failed:", err);
      setPhase("review");
    }
  };

  const handleFineTune = async () => {
    setPhase("generating");
    setGenStep(0);
    setProgress(10);
    try {
      await onFineTune();
    } catch (err) {
      console.error("Save failed:", err);
      setPhase("review");
    }
  };

  // ── Generating Phase ──
  if (phase === "generating") return (
    <div style={{ maxWidth: 640, margin: "0 auto", textAlign: "center", padding: isMobile ? "40px 20px" : "60px 40px" }}>
      <div style={{ width: 72, height: 72, borderRadius: 20, background: "#1a1a1a", margin: "0 auto 28px", display: "flex", alignItems: "center", justifyContent: "center", color: "#FFF", animation: "pulse 2s ease-in-out infinite" }}>{Icons.zap}</div>
      <h2 style={{ fontSize: isMobile ? 22 : 26, fontWeight: 700, color: "#1a1a1a", margin: "0 0 8px", fontFamily: "'DM Sans',sans-serif" }}>Building Your Schedule</h2>
      <p style={{ fontSize: 14, color: "#999", margin: "0 0 32px", fontFamily: "'DM Sans',sans-serif" }}>Writing data and generating shifts...</p>
      <div style={{ maxWidth: 400, margin: "0 auto 24px" }}>
        <div style={{ width: "100%", height: 6, borderRadius: 3, background: "#F0F0F0", overflow: "hidden" }}>
          <div style={{ width: `${progress}%`, height: "100%", borderRadius: 3, background: "linear-gradient(90deg,#1a1a1a,#2D8F5E)", transition: "width 0.3s ease" }} />
        </div>
        <div style={{ fontSize: 12, fontWeight: 600, color: "#BBB", fontFamily: "'DM Sans',sans-serif", marginTop: 8, textAlign: "right" }}>{Math.round(progress)}%</div>
      </div>
      <div style={{ maxWidth: 320, margin: "0 auto", display: "flex", flexDirection: "column", gap: 6, textAlign: "left" }}>
        {GEN_STEPS.map((s, i) => {
          const done = i < genStep, act = i === genStep;
          return (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 0", opacity: done ? 0.5 : act ? 1 : 0.25, transition: "opacity 0.3s ease" }}>
              <div style={{ width: 20, height: 20, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", background: done ? "#2D8F5E" : act ? "#1a1a1a" : "#E5E5E5", color: "#FFF", fontSize: 9 }}>
                {done ? Icons.check : act ? <div style={{ width: 8, height: 8, border: "2px solid #FFF", borderTop: "2px solid transparent", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} /> : null}
              </div>
              <span style={{ fontSize: 13, fontWeight: act ? 600 : 400, color: act ? "#1a1a1a" : "#999", fontFamily: "'DM Sans',sans-serif" }}>{s.label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );

  // ── Success Phase ──
  if (phase === "success" && result) return (
    <div style={{ maxWidth: 640, margin: "0 auto", textAlign: "center", animation: "fadeUp 0.5s ease-out" }}>
      <div style={{ position: "relative", display: "inline-block", marginBottom: 24 }}>
        <div style={{ width: 80, height: 80, borderRadius: 22, background: "#2D8F5E", margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "center", color: "#FFF", animation: "popIn 0.5s cubic-bezier(0.4,0,0.2,1) 0.1s both" }}>{Icons.checkCircle}</div>
        {["#E85D3A", "#6C5CE7", "#0891B2", "#2D8F5E", "#D4742C", "#8B6914"].map((c, i) => <div key={i} style={{ position: "absolute", width: 6, height: 6, borderRadius: 3, background: c, top: "50%", left: "50%", animation: `confetti${(i % 3) + 1} 0.8s ease-out ${0.3 + i * 0.08}s both` }} />)}
      </div>
      <h2 style={{ fontSize: isMobile ? 24 : 30, fontWeight: 700, color: "#1a1a1a", margin: "0 0 6px", fontFamily: "'DM Sans',sans-serif" }}>Schedule Ready</h2>
      <p style={{ fontSize: isMobile ? 14 : 15, color: "#888", margin: "0 0 32px", fontFamily: "'DM Sans',sans-serif" }}>Your first week has been generated</p>

      {/* Score */}
      <div style={{ display: "inline-flex", flexDirection: "column", alignItems: "center", padding: "20px 40px", borderRadius: 20, background: "#FFF", border: "1px solid #EBEBEB", boxShadow: "0 4px 20px rgba(0,0,0,0.04)", marginBottom: 28 }}>
        <div style={{ fontSize: 10, fontWeight: 600, color: "#BBB", textTransform: "uppercase", letterSpacing: "0.08em", fontFamily: "'DM Sans',sans-serif", marginBottom: 4 }}>Schedule Score</div>
        <div style={{ fontSize: 56, fontWeight: 700, color: scoreCount >= 80 ? "#2D8F5E" : scoreCount >= 60 ? "#D4742C" : "#E85D3A", fontFamily: "'DM Sans',sans-serif", lineHeight: 1 }}>{scoreCount}</div>
        <div style={{ fontSize: 13, color: "#BBB", fontFamily: "'DM Sans',sans-serif", marginTop: 4 }}>out of 100</div>
      </div>

      {/* Stats */}
      <div style={{ display: "flex", gap: isMobile ? 8 : 12, justifyContent: "center", flexWrap: "wrap", marginBottom: 32, padding: "0 16px" }}>
        {[
          { l: "Shifts Filled", v: `${result.shiftsFilled}/${result.shiftsTotal || result.shiftsFilled}` },
          { l: "Employees", v: result.employeesScheduled },
          { l: "Conflicts", v: String(result.conflicts), g: result.conflicts === 0 },
        ].map(({ l, v, g }) =>
          <div key={l} style={{ padding: "14px 20px", borderRadius: 14, background: "#FFF", border: "1px solid #EBEBEB", minWidth: isMobile ? 100 : 120 }}>
            <div style={{ fontSize: 10, color: "#BBB", fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.06em", fontFamily: "'DM Sans',sans-serif" }}>{l}</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: g ? "#2D8F5E" : "#1a1a1a", fontFamily: "'DM Sans',sans-serif", marginTop: 2 }}>{v}</div>
          </div>
        )}
      </div>

      {/* Deductions */}
      {result.deductions && result.deductions.length > 0 && (
        <div style={{ maxWidth: 400, margin: "0 auto 24px", textAlign: "left" }}>
          {result.deductions.map((d, i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", fontSize: 13, fontFamily: "'DM Sans',sans-serif" }}>
              <span style={{ color: "#888" }}>{d.text}</span>
              <span style={{ color: "#E85D3A", fontWeight: 600 }}>{d.pts}</span>
            </div>
          ))}
        </div>
      )}

      {/* CTA */}
      <button onClick={() => onBuildSchedule('viewSchedule')} style={{ display: "inline-flex", alignItems: "center", gap: 10, padding: isMobile ? "16px 36px" : "14px 40px", borderRadius: 14, border: "none", background: "#1a1a1a", cursor: "pointer", fontSize: isMobile ? 16 : 15, fontWeight: 600, color: "#FFF", fontFamily: "'DM Sans',sans-serif", boxShadow: "0 4px 16px rgba(0,0,0,0.15)", transition: "all 0.2s ease" }}
        onMouseEnter={e => { e.currentTarget.style.background = "#333"; e.currentTarget.style.transform = "translateY(-2px)"; }}
        onMouseLeave={e => { e.currentTarget.style.background = "#1a1a1a"; e.currentTarget.style.transform = "translateY(0)"; }}>
        <span style={{ display: "flex" }}>{Icons.calendar}</span>View Schedule<span style={{ display: "flex" }}>{Icons.arrowRight}</span>
      </button>
    </div>
  );

  // ── Review Phase with Two Path Cards ──
  return (
    <div style={{ maxWidth: 720, margin: "0 auto" }}>
      <div style={{ marginBottom: isMobile ? 24 : 32, textAlign: "center" }}>
        <div style={{ width: 52, height: 52, borderRadius: 14, background: "#1a1a1a", margin: "0 auto 16px", display: "flex", alignItems: "center", justifyContent: "center", color: "#FFF" }}>{Icons.zap}</div>
        <h1 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 700, color: "#1a1a1a", margin: "0 0 4px", fontFamily: "'DM Sans',sans-serif", letterSpacing: "-0.02em" }}>Ready to Generate</h1>
        <p style={{ fontSize: isMobile ? 13 : 15, color: "#888", margin: 0 }}>Review your setup, then choose how to proceed</p>
      </div>

      {/* Summary Cards */}
      <div style={{ display: "flex", flexDirection: "column", gap: isMobile ? 12 : 14, marginBottom: isMobile ? 24 : 32 }}>
        {[
          { icon: Icons.store, title: data.restaurant.name || "Restaurant", step: 1, items: [{ label: "Manager", value: data.restaurant.manager || "\u2014" }] },
          { icon: Icons.clock, title: "Hours & Periods", step: 2, items: [{ label: "Open Days", value: `${openDays}/7` }, { label: "Periods", value: data.dayData.reduce((s, d) => s + (d.isOpen ? Object.values(d.periods).filter(p => p.active).length : 0), 0) }] },
          { icon: Icons.grid, title: "Roles & Staffing", step: 3, items: activeRoles.map(r => ({ label: r.name, value: Object.entries(data.staffing).filter(([k]) => k.includes(r.name)).reduce((s, [, v]) => s + v, 0), color: r.color })) },
          { icon: Icons.users, title: "Team", step: 4, items: [{ label: "Employees", value: empCount }, { label: "Shifts/Week", value: totalShifts }] },
        ].map(card => (
          <div key={card.step} style={{ background: "#FFF", borderRadius: 16, padding: isMobile ? "16px" : "20px 24px", border: "1px solid #EBEBEB", boxShadow: "0 1px 4px rgba(0,0,0,0.03)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: "#F7F7F6", display: "flex", alignItems: "center", justifyContent: "center", color: "#666" }}>{card.icon}</div>
              <div>
                <div style={{ fontSize: 10, fontWeight: 600, color: "#BBB", textTransform: "uppercase", letterSpacing: "0.06em", fontFamily: "'DM Sans',sans-serif" }}>Step {card.step}</div>
                <div style={{ fontSize: 15, fontWeight: 600, color: "#1a1a1a", fontFamily: "'DM Sans',sans-serif" }}>{card.title}</div>
              </div>
              <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8 }}>
                <div style={{ width: 22, height: 22, borderRadius: "50%", background: "#2D8F5E", display: "flex", alignItems: "center", justifyContent: "center", color: "#FFF" }}>{Icons.check14}</div>
                <button onClick={() => onGoToStep(card.step - 1)} style={{ display: "flex", alignItems: "center", gap: 4, padding: "5px 10px", borderRadius: 6, border: "1px solid #E5E5E5", background: "#FFF", cursor: "pointer", fontSize: 11, fontWeight: 500, color: "#999", fontFamily: "'DM Sans',sans-serif", transition: "all 0.15s ease", WebkitTapHighlightColor: "transparent" }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = "#CCC"; e.currentTarget.style.color = "#666"; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = "#E5E5E5"; e.currentTarget.style.color = "#999"; }}>
                  <span style={{ display: "flex" }}>{Icons.edit}</span>Edit
                </button>
              </div>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {card.items.map(({ label, value, color }) => (
                <div key={label} style={{ padding: "6px 12px", borderRadius: 8, background: color ? `${color}08` : "#F7F7F6", border: `1px solid ${color ? `${color}20` : "#EBEBEB"}` }}>
                  <div style={{ fontSize: 10, fontWeight: 500, color: "#BBB", textTransform: "uppercase", letterSpacing: "0.04em", fontFamily: "'DM Sans',sans-serif" }}>{label}</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: color || "#1a1a1a", fontFamily: "'DM Sans',sans-serif", marginTop: 1 }}>{value}</div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* ── Two Path Cards ── */}
      <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: 16 }}>
        {/* Build Schedule Now */}
        <button onClick={handleBuildNow} style={{ flex: 1, background: "#FFF", borderRadius: 16, padding: isMobile ? "24px 20px" : "28px 24px", border: "2px solid #1a1a1a", boxShadow: "0 2px 12px rgba(0,0,0,0.06)", cursor: "pointer", textAlign: "left", transition: "all 0.2s ease", WebkitTapHighlightColor: "transparent" }}
          onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 8px 24px rgba(0,0,0,0.1)"; }}
          onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 2px 12px rgba(0,0,0,0.06)"; }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
            <div style={{ width: 44, height: 44, borderRadius: 12, background: "#1a1a1a", display: "flex", alignItems: "center", justifyContent: "center", color: "#FFF" }}>{Icons.zap}</div>
            <div>
              <div style={{ fontSize: isMobile ? 17 : 18, fontWeight: 700, color: "#1a1a1a", fontFamily: "'DM Sans',sans-serif" }}>Build Schedule Now</div>
              <div style={{ fontSize: 12, color: "#2D8F5E", fontWeight: 600, fontFamily: "'DM Sans',sans-serif" }}>Recommended</div>
            </div>
          </div>
          <div style={{ fontSize: 13, color: "#888", fontFamily: "'DM Sans',sans-serif", lineHeight: 1.6 }}>
            Generate your first week's schedule immediately using the roles and staffing you just configured. You can always adjust it after.
          </div>
        </button>

        {/* Fine-Tune Team First */}
        <button onClick={handleFineTune} style={{ flex: 1, background: "#FFF", borderRadius: 16, padding: isMobile ? "24px 20px" : "28px 24px", border: "2px solid #EBEBEB", boxShadow: "0 1px 4px rgba(0,0,0,0.03)", cursor: "pointer", textAlign: "left", transition: "all 0.2s ease", WebkitTapHighlightColor: "transparent" }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = "#CCC"; e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 8px 24px rgba(0,0,0,0.06)"; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = "#EBEBEB"; e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 1px 4px rgba(0,0,0,0.03)"; }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
            <div style={{ width: 44, height: 44, borderRadius: 12, background: "#F7F7F6", display: "flex", alignItems: "center", justifyContent: "center", color: "#666" }}>{Icons.users}</div>
            <div>
              <div style={{ fontSize: isMobile ? 17 : 18, fontWeight: 700, color: "#1a1a1a", fontFamily: "'DM Sans',sans-serif" }}>Fine-Tune Team First</div>
              <div style={{ fontSize: 12, color: "#999", fontWeight: 500, fontFamily: "'DM Sans',sans-serif" }}>Set up details before generating</div>
            </div>
          </div>
          <div style={{ fontSize: 13, color: "#888", fontFamily: "'DM Sans',sans-serif", lineHeight: 1.6, marginBottom: 14 }}>
            Head to the Employees page to configure each team member before building your schedule:
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {[
              { label: "Availability", desc: "Which days each person can work" },
              { label: "Recurring Shifts", desc: "Lock in fixed weekly shifts" },
              { label: "Target & Max Hours", desc: "Weekly hour preferences" },
              { label: "Preferred Shifts", desc: "Morning, evening, or flexible" },
            ].map(item => (
              <div key={item.label} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div style={{ width: 4, height: 4, borderRadius: "50%", background: "#CCC", flexShrink: 0 }} />
                <span style={{ fontSize: 12, fontFamily: "'DM Sans',sans-serif" }}>
                  <span style={{ fontWeight: 600, color: "#555" }}>{item.label}</span>
                  <span style={{ color: "#BBB" }}> — {item.desc}</span>
                </span>
              </div>
            ))}
          </div>
        </button>
      </div>
    </div>
  );
}
